/**
 * RESPONSIBILITY: one event's page, with the pencil that edits that event.
 *
 * The pencil opens the events section narrowed to this row — see `focus`. Opening the
 * whole list from here would be technically the same document and practically useless:
 * an editor who arrived from an event's own page wants that event, not eighty rows to
 * find it among.
 *
 * The row is looked up again inside the section rather than taken from the caller, so
 * the page follows the draft while the dialog is open and the editor sees what they are
 * typing rather than what was there when they opened it.
 */
import { idOf, rowById } from '@framework/core';
import type { EventRow } from '@app/sections';
import { SectionName } from '@app/sections';
import { SectionUpdate } from '../cms';
import { EventDetail } from '../site';

export function EventPage({ e, all }: { e: EventRow | undefined; all: readonly EventRow[] }) {
  // An address that matches nothing gets the "moved on" page. It keeps its pencil: the
  // words on it are content like any other, and the whole list is the right thing to
  // open — there is no row to narrow to, which is exactly the situation.
  if (!e) {
    return (
      <SectionUpdate name={SectionName.Events}>
        {(c) => <EventDetail c={c} e={undefined} all={all} />}
      </SectionUpdate>
    );
  }

  return (
    <SectionUpdate name={SectionName.Events} focus={{ field: 'events', rowId: idOf(e) }}>
      {(c) => <EventDetail c={c} e={rowById(c.events ?? [], idOf(e)) ?? e} all={c.events ?? all} />}
    </SectionUpdate>
  );
}
